<script module lang="ts">
    type ButtonType = "COLLAPSED" | "EXPANDED" | "BANNER";
</script>

<script lang="ts">
    import { onMount, tick } from "svelte";
    import { FLOATING_BUTTON_DESCRIPTION_DELAY, FLOATING_BUTTON_LOCAL_STORAGE_KEY } from "../../config";
    import { trackFloatingActionButtonClick, trackBannerClick } from "../../stores/interaction-tracking.svelte.js";
    import { fade, slide } from "svelte/transition";

    let { href, canBeBanner, canBeExpanded } = $props();
    const COLOR_PURPLE_140 = "var(--oc-base-color-purple-140, #522eb7)";

    let isInitialized = $state(false);
    let buttonState: ButtonType = $state("COLLAPSED");
    let latestScrollY = 0;
    let shouldUseTransition = $derived.by(() => buttonState !== "BANNER" && isInitialized);

    function closeTooltip() {
        buttonState = canBeExpanded ? "EXPANDED" : "COLLAPSED";
        localStorage?.setItem(FLOATING_BUTTON_LOCAL_STORAGE_KEY, "true");
    }

    function updateButtonState(newState: ButtonType) {
        if (buttonState !== "BANNER") {
            buttonState = newState;
        }
    }

    const scrollListener = () => {
        /*                         */
        const currentScrollY = window.scrollY >= 0 ? window.scrollY : 0;
        const isAtBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 20;

        /*                                                   */
        if (isAtBottom) {
            updateButtonState("EXPANDED");
            latestScrollY = currentScrollY;
            return;
        }

        const isScrollingDown = currentScrollY > latestScrollY;
        const isScrollingUp = currentScrollY < latestScrollY - 16;

        if (isScrollingDown) {
            latestScrollY = currentScrollY;
            if (buttonState !== "BANNER") {
                buttonState = "COLLAPSED";
            }
        } else if (isScrollingUp) {
            latestScrollY = currentScrollY;
            updateButtonState("EXPANDED");
        }
    };

    function expandedButtonSlide(node: Element) {
        if (!shouldUseTransition) {
            return {};
        }
        return slide(node, { duration: 200, axis: "x" });
    }

    function expandedButtonFade(node: Element) {
        if (!shouldUseTransition) {
            return {};
        }
        return fade(node, { duration: 400 });
    }

    onMount(() => {
        if (canBeExpanded) {
            document.addEventListener("scroll", scrollListener);
        }

        latestScrollY = window.scrollY;
        if (latestScrollY === 0 && canBeExpanded) {
            buttonState = "EXPANDED";
        }

        setTimeout(() => {
            if (canBeBanner) {
                buttonState = "BANNER";
            }
        }, FLOATING_BUTTON_DESCRIPTION_DELAY);

        tick().then(() => {
            isInitialized = true;
        });

        () => document.removeEventListener("scroll", scrollListener);
    });
</script>

<div class="floating-action-button-container" class:floating-banner={buttonState === "BANNER"}>
    <div class="floating-action-button-wrapper">
        {#if buttonState === "BANNER"}
            <div data-testid="floating-action-button-banner" class="floating-action-button-description">
                <p class="oc-headline-50">NEU! Unser OTTO KI-Assistent!</p>
                <p class="oc-copy-100">Erhalte persönliche Beratung und finde ganz einfach die Produkte, die zu dir passen.</p>
                <oc-link-v2
                    as-button
                    variant="underlined"
                    onclick={() => {
                        if (href) {
                            trackBannerClick();
                            window.location.href = href;
                        }
                    }}
                >
                    Starte jetzt deinen Dialog
                </oc-link-v2>
            </div>
        {/if}
        <div class="floating-action-button-buttons">
            {#if buttonState === "BANNER"}
                <oc-icon-button-v2 variant="transparent" icon-color="#fff" icon="close" oc-aria-label="Chat Hinweis schließen" onclick={closeTooltip}></oc-icon-button-v2>
            {/if}

            <button
                type="button"
                tabindex="0"
                aria-disabled={false}
                onclick={() => {
                    if (href) {
                        trackFloatingActionButtonClick();
                        window.location.href = href;
                    }
                }}
                class:within-banner={buttonState === "BANNER"}
                class="floating-action-button oc-p-75 oc-m-0"
                aria-label="OTTO KI-Assistent"
                data-testid="floating-action-button"
            >
                <oc-icon-v1 size={"100"} color={buttonState === "BANNER" ? COLOR_PURPLE_140 : "white"} type="speech-bubble-sparkles"></oc-icon-v1>
                {#if buttonState === "EXPANDED"}
                    <div transition:expandedButtonSlide>
                        <span transition:expandedButtonFade class="oc-copy-125 oc-pl-50">KI-Assistent</span>
                    </div>
                {/if}
            </button>
        </div>
    </div>
</div>

<style lang="scss">
    @use "@otto-ec/design-tokens/tokens";
    @use "@otto-ec/otto-components-utils/scss/_mixins/breakpoint";

    .floating-action-button-container {
        width: 100%;
        max-width: 1152px;
        display: flex;
        justify-content: end;
        position: fixed;
        bottom: 0;
        pointer-events: none;
        margin-bottom: 20px;
        gap: tokens.$oc-semantic-spacing-100;
        z-index: 101;
        @media (min-width: 77em) {
            width: 84%;
        }

        &.floating-banner {
            margin-bottom: tokens.$oc-semantic-spacing-25;

            .floating-action-button-wrapper {
                filter: tokens.$oc-semantic-shadow-sticky;
            }

            .floating-action-button-wrapper {
                padding: tokens.$oc-semantic-spacing-100 tokens.$oc-semantic-spacing-50 tokens.$oc-semantic-spacing-100 tokens.$oc-semantic-spacing-100;
                gap: tokens.$oc-semantic-spacing-100;
                max-width: 305px;
                margin-right: tokens.$oc-semantic-spacing-50;
            }
        }
    }

    .floating-action-button-wrapper {
        border-radius: tokens.$oc-semantic-border-radius-100;
        display: flex;
        flex-direction: row;
        background-color: tokens.$oc-base-color-purple-140;
        pointer-events: all;
        margin-right: tokens.$oc-semantic-spacing-100;
        --oc-semantic-color-text-default: tokens.$oc-base-color-white;
    }

    .floating-action-button-buttons {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        align-items: end;
        gap: tokens.$oc-semantic-spacing-50;
    }

    .floating-action-button-description {
        color: tokens.$oc-base-color-white;
        display: flex;
        flex-direction: column;
        text-align: right;
    }

    .floating-action-button {
        border: none;
        border-radius: tokens.$oc-semantic-border-radius-100;
        display: flex;
        align-items: center;
        flex-direction: row;
        cursor: pointer;
        transition: width 0.3s ease;
        width: auto;
        &.within-banner {
            background-color: tokens.$oc-base-color-white-100;
        }

        &:not(.within-banner) {
            background-color: tokens.$oc-base-color-purple-140;
            filter: tokens.$oc-semantic-shadow-sticky;
        }
        &:active {
            background-color: var(--oc-component-icon-button-secondary-on-color-background-color-active);
        }

        span {
            color: tokens.$oc-base-color-white;
            white-space: nowrap;
            overflow-x: hidden;
        }
    }
</style>
